// pages/admin/program/ProgramList.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { programService, Program } from '../../../firebase/programFirestoreService';
import { FaEdit, FaTrash, FaEye, FaCalendarPlus } from '../../../utils/iconUtils';
import { useUI } from '../../../components/common/UIMessageProvider';

const ProgramList: React.FC = () => {
  const auth = useAuth();
  const canViewPrograms = auth.canPerformAction('programs', 'view');
  const canCreatePrograms = auth.canPerformAction('programs', 'create');
  const canEditPrograms = auth.canPerformAction('programs', 'edit');
  const canDeletePrograms = auth.canPerformAction('programs', 'delete');
  const canPublishPrograms = auth.canPerformAction('programs', 'publish') || auth.canPerformAction('programs', 'edit');

  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const ui = useUI();

  useEffect(() => {
    loadPrograms();
  }, [filter]);

  const loadPrograms = async () => {
    setLoading(true);
    try {
      let statusFilter;
      switch (filter) {
        case 'published':
          statusFilter = ['published'];
          break;
        case 'draft':
          statusFilter = ['draft'];
          break;
        case 'ongoing':
          statusFilter = ['ongoing'];
          break;
        default:
          statusFilter = undefined;
      }
      
      const data = await programService.getPrograms(50, statusFilter);
      setPrograms(data);
    } catch (error) {
      console.error('Error loading programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await ui.askConfirm({
      title: 'Delete Program',
      message: 'Are you sure you want to delete this program?',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    if (!ok) return;
    try {
      await programService.softDeleteProgram(id);
      await loadPrograms();
      ui.showMessage('Program deleted', { type: 'success' });
    } catch (error) {
      console.error('Error deleting program:', error);
      ui.showMessage('Failed to delete program', { type: 'error' });
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', label: 'Draft' },
      published: { color: 'bg-blue-100 text-blue-800', label: 'Published' },
      ongoing: { color: 'bg-green-100 text-green-800', label: 'Ongoing' },
      completed: { color: 'bg-purple-100 text-purple-800', label: 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">All Programs</h2>
          <p className="text-gray-600 text-xs">Manage and organize your programs</p>
        </div>
        
        <div className="flex gap-3">
          {canCreatePrograms && (
            <Link
              to="/admin/programs/create"
              className="inline-flex items-center gap-2 px-3 py-1 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              <FaCalendarPlus className="h-4 w-4" /> Create Program
            </Link>
          )}
          
          {canPublishPrograms && (
            <Link
              to="/admin/programs/publish"
              className="inline-flex items-center gap-2 px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              <FaEye className="h-4 w-4" /> Publish Program
            </Link>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-3 rounded-lg shadow">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-md ${
              filter === 'all' 
                ? 'bg-primary-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All Programs
          </button>
          <button
            onClick={() => setFilter('published')}
            className={`px-4 py-2 rounded-md ${
              filter === 'published' 
                ? 'bg-primary-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Published
          </button>
          <button
            onClick={() => setFilter('draft')}
            className={`px-4 py-2 rounded-md ${
              filter === 'draft' 
                ? 'bg-primary-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Drafts
          </button>
          <button
            onClick={() => setFilter('ongoing')}
            className={`px-4 py-2 rounded-md ${
              filter === 'ongoing' 
                ? 'bg-primary-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Ongoing
          </button>
        </div>
      </div>

      {/* Programs Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : programs.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No programs found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Program
                  </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dates
                  </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Registrations
                  </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {programs.map((program) => (
                  <tr key={program.id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap">
                      <div>
                        <div className="font-medium text-gray-900">{program.title}</div>
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {program.description}
                        </div>
                      </div>
                    </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {program.startDate?.toDate().toLocaleDateString()}
                      </div>
                      <div className="text-sm text-gray-500">
                        to {program.endDate?.toDate().toLocaleDateString()}
                      </div>
                    </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                      {getStatusBadge(program.status)}
                    </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {program.registeredCount}
                        {program.capacity && ` / ${program.capacity}`}
                      </div>
                    </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        {canEditPrograms && (
                          <Link
                            to={`/admin/programs/edit/${program.id}`}
                            className="text-blue-600 hover:text-blue-900"
                          >
                                    <FaEdit className="h-4 w-4" />
                          </Link>
                        )}
                        
                        <Link
                          to={`/programs/${program.id}`}
                          target="_blank"
                          className="text-green-600 hover:text-green-900"
                        >
                                  <FaEye className="h-4 w-4" />
                        </Link>
                        
                        {canDeletePrograms && (
                          <button
                            onClick={() => program.id && handleDelete(program.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                                    <FaTrash className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgramList;